// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { useCaseShell } from '../../app/bygning/CaseShellContext';
import { ShellNotices } from '../../app/bygning/ShellNotices';
import { OVERBLIK_PATH } from '../../app/links';
import { ALL_CASES_PATH, EXPERT_ENTRIES, expertEntryFor } from '../../app/navigation';
import { modePath, visibleModes } from '../../bygning/modes';
import { useCanEdit } from '../../app/CaseRoleContext';
import { accountScope, buildCommands, filterCommands } from '../../bygning/palette';
import { CommandPalette, type PaletteItem } from '../../components/bygning/CommandPalette';
import { Crumb } from '../../components/bygning/Crumb';
import { PaletteButton } from '../../components/bygning/TopBar';
import { Logo } from '../../components/Logo';
import styles from './ToolFrame.module.css';

/** The ToolFrame title for a path: the expert page it belongs to. */
function pageTitle(pathname: string): string | undefined {
  return expertEntryFor(pathname)?.label;
}

/**
 * The minimal frame for the expert pages (Pipeline, Billeder, Geometri, …):
 * a slim bar with the crumb, the way back to the building and the ⌘K
 * palette, then the page in `<main>`. No
 * Sidebar (decision 8). Used as a layout route (renders the `<Outlet>`) or
 * around explicit children.
 */
export function ToolFrame({ children }: { children?: ReactNode }) {
  const shell = useCaseShell();
  const location = useLocation();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const canEdit = useCanEdit();
  const title = pageTitle(location.pathname);

  // The case store lives in the Bygning shell, so only navigation and the
  // read-only actions are offered here.
  const commands = useMemo(
    () =>
      buildCommands({
        modes: visibleModes(canEdit),
        types: [],
        samples: [],
        rooms: [],
        experts: EXPERT_ENTRIES,
        canEdit: false,
        account: accountScope(shell.user, shell.viaShareLink),
        // No Ground here: no Modelindstillinger and no viewer commands.
      }).filter((c) => c.group !== 'Model' && !(c.target.kind === 'action' && c.target.action === 'settings')),
    [canEdit, shell.user, shell.viaShareLink],
  );
  const search = useCallback(
    (query: string): PaletteItem[] => filterCommands(commands, query).map((c) => ({ id: c.id, label: c.label, hint: c.hint })),
    [commands],
  );
  const run = useCallback(
    (id: string) => {
      const t = commands.find((c) => c.id === id)?.target;
      if (!t) return;
      if (t.kind === 'mode') navigate(modePath(t.mode));
      else if (t.kind === 'select') navigate(modePath(t.mode));
      else if (t.kind === 'navigate') navigate(t.to);
      else if (t.kind === 'leave') window.location.assign(t.href);
      else if (t.kind !== 'action') return;
      else if (t.action === 'toggle-theme') shell.theme.setPreference(shell.theme.resolved === 'dark' ? 'light' : 'dark');
      else if (t.action === 'export-csv') {
        const a = document.createElement('a');
        a.href = api.csvExportUrl();
        a.download = '';
        a.click();
      }
    },
    [commands, navigate, shell.theme],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const name = shell.projectName;
    document.title = [title, name, 'ReUseX'].filter(Boolean).join(' · ');
  }, [title, shell.projectName]);

  return (
    <div className={styles.frame}>
      <header className={styles.bar}>
        <Logo size={14} />
        <Crumb
          casesHref={shell.viaShareLink ? undefined : ALL_CASES_PATH}
          caseName={shell.projectName}
          here={title ?? 'Ekspert'}
        />
        <div className={styles.right}>
          <Link className={styles.back} to={OVERBLIK_PATH}>
            Tilbage til bygningen
          </Link>
          <PaletteButton onClick={() => setPaletteOpen(true)} />
        </div>
      </header>
      <ShellNotices variant="inline" />
      <main className={styles.main} tabIndex={-1}>
        {children ?? <Outlet />}
      </main>
      <CommandPalette open={paletteOpen} search={search} onRun={run} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
